import { useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { apiErrorMessage } from "../../services/apiError";
import { nearestPoint } from "../training/lib/chart";
import { useElementWidth } from "../training/lib/useElementWidth";
import { SERIES_PAGE_CAP } from "./useScoreboardSeries";
import { formatDay, formatInstant } from "./lib/format";
import { formatCents, formatUsd, scaled } from "./lib/money";
import { axisDollars, scoreboardChart } from "./lib/scoreboardChart";

const HEIGHT = 200;
// Room for the y labels ("$10.25K") on the left and the month labels below.
const MARGIN = { top: 16, right: 16, bottom: 26, left: 56 };
const DOT = 4; // r: an 8 px marker
const REACH = 12; // the plot's tap target reaches this far past its edges
const ACCOUNT = "var(--primary)";
const SHADOW = "var(--chart-benchmark)";
const DASH = "6 4";

/** "$555.33 behind", "$345.45 ahead", "level" — from whole cents. */
function standing(difference) {
  if (difference > 0n) return `${formatCents(difference)} ahead`;
  if (difference < 0n) return `${formatCents(-difference)} behind`;
  return "level";
}

const signedCents = (cents) => (cents > 0n ? `+${formatCents(cents)}` : formatCents(cents));
const count = (n) => n.toLocaleString("en-US");

/** A swatch for the legend: the line as the chart draws it. */
function Swatch({ color, dashed = false }) {
  return (
    <svg aria-hidden="true" width={24} height={8} className="shrink-0">
      <line x1={1} x2={23} y1={4} y2={4} stroke={color} strokeWidth={2} strokeDasharray={dashed ? DASH : undefined} />
    </svg>
  );
}

Swatch.propTypes = { color: PropTypes.string.isRequired, dashed: PropTypes.bool };

/**
 * The scoreboard line (spec §5.5, D15: a hand-built SVG like Training's
 * Progress): the account and the same money held in the benchmark at every
 * snapshot. PR 4 has no series, so each point is worked out in the browser
 * with the scoreboard's own rules (lib/scoreboard.js) from GET snapshots/,
 * contributions/ and benchmark-distributions/ — and its last point is
 * checked against GET scoreboard/: a mismatch is said loudly, the
 * scoreboard's figures being the server's.
 *
 * Words first, then the chart, which draws only what the words and the
 * table say. The SVG is aria-hidden with ONE tap target: a tap or click
 * picks the nearest snapshot and says its figures under the chart (as
 * LiftChart does — per-dot targets are too small on a phone). *Show as a
 * table* is the keyboard and screen-reader path: every point, newest first.
 *
 * `line` is useScoreboardSeries' state; `board` GET scoreboard/'s data (null
 * before the first snapshot or when it failed).
 */
function ScoreboardLine({ line, board }) {
  const id = useId();
  const box = useRef(null);
  const svg = useRef(null);
  const [picked, setPicked] = useState(null); // a snapshot id, from a tap
  const [tableOpen, setTableOpen] = useState(false);
  const width = useElementWidth(box);
  const symbol = board?.benchmark_symbol ?? null;
  const shadowName = `${symbol ?? "Benchmark"} shadow`;
  const { series } = line;
  const chart = useMemo(
    () => scoreboardChart(series ?? [], { width, height: HEIGHT, margin: MARGIN }),
    [series, width],
  );

  let body;
  if (line.error) {
    body = (
      <Alert variant="destructive" role="alert">
        <AlertDescription className="flex flex-wrap items-center gap-2">
          <span>Couldn&apos;t read the line&apos;s records: {apiErrorMessage(line.error, "they didn't load.")}</span>{" "}
          <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={line.retry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (line.loading && !series) {
    body = (
      <p role="status" className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Reading the snapshots…
      </p>
    );
  } else if (line.incomplete) {
    body = (
      <p className="m-0 text-sm">
        No line: there are more contributions or distributions than this page reads ({count(SERIES_PAGE_CAP * 100)}{" "}
        of each), and a shadow missing one would be wrong. The scoreboard above has the latest figures.
      </p>
    );
  } else if (!series) {
    body = (
      <p className="m-0 text-sm">
        No line: a snapshot, contribution or distribution couldn&apos;t be read, so the line can&apos;t be worked
        out. The scoreboard above has the latest figures.
      </p>
    );
  } else if (series.length === 0) {
    body = (
      <p className="m-0 text-sm">
        No snapshot yet. The line starts at your executor&apos;s first snapshot and draws once there are two: the
        account and the same money held in the benchmark, at every snapshot.
      </p>
    );
  } else {
    const last = series.at(-1);
    const latest = chart.points.at(-1);
    const placedPick = chart.points.find((placed) => placed.point.id === picked) ?? null;
    // The scoreboard's own figures, when it was read at the same snapshot.
    const checked = board && board.snapshot === last.id;
    const matches =
      checked &&
      scaled(board.account_value, 2) === last.account &&
      scaled(board.shadow_value, 2) === last.shadow &&
      scaled(board.net_contributions, 2) === last.net &&
      scaled(board.difference, 2) === last.difference;
    const pick = (event) => {
      const left = svg.current?.getBoundingClientRect().left ?? 0;
      const index = nearestPoint(chart.points, event.clientX - left);
      if (index !== -1) setPicked(chart.points[index].point.id);
    };
    body = (
      <>
        <p className="m-0 text-sm tabular-nums">
          {series.length === 1
            ? `One snapshot so far, ${formatDay(last.asOf)}: the line draws from the second.`
            : `${count(series.length)} snapshots, ${formatDay(series[0].asOf)} to ${formatDay(last.asOf)}.`}{" "}
          At the latest: account {formatCents(last.account)}, {shadowName} {formatCents(last.shadow)} —{" "}
          {standing(last.difference)}.
        </p>
        {line.truncated && (
          <p className="m-0 text-sm">
            Showing the newest {count(line.snapshotsShown)} of {count(line.snapshotsTotal)} snapshots.
          </p>
        )}
        <ul aria-label="Legend" className="mt-2 mb-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-sm">
          <li className="flex items-center gap-2">
            <Swatch color={ACCOUNT} />
            <span>Account</span>
          </li>
          <li className="flex items-center gap-2">
            <Swatch color={SHADOW} dashed />
            <span>{shadowName}</span>
          </li>
        </ul>
        <div ref={box} aria-hidden="true" data-chart className="mt-2 text-muted-foreground">
          <svg ref={svg} width={width} height={HEIGHT} viewBox={`0 0 ${width} ${HEIGHT}`} className="block" fontSize={11}>
            {chart.yTicks.map((tick) => (
              <g key={`y${tick.value}`}>
                <line
                  x1={chart.plot.left}
                  x2={chart.plot.right}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="var(--border)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text x={chart.plot.left - 6} y={tick.y} textAnchor="end" dominantBaseline="middle" fill="currentColor">
                  {axisDollars(tick.value)}
                </text>
              </g>
            ))}
            {chart.xTicks.map((tick) => (
              <g key={`x${tick.day}`}>
                <line
                  x1={tick.x}
                  x2={tick.x}
                  y1={chart.plot.bottom}
                  y2={chart.plot.bottom + 4}
                  stroke="var(--border)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
                <text x={tick.x} y={chart.plot.bottom + 17} textAnchor="middle" fill="currentColor">
                  {tick.label}
                </text>
              </g>
            ))}
            {placedPick && (
              <line
                x1={placedPick.x}
                x2={placedPick.x}
                y1={chart.plot.top}
                y2={chart.plot.bottom}
                stroke="currentColor"
                strokeWidth={1}
                strokeDasharray="2 3"
                pointerEvents="none"
              />
            )}
            {chart.shadowPath && (
              <path
                data-line="shadow"
                d={chart.shadowPath}
                fill="none"
                stroke={SHADOW}
                strokeWidth={2}
                strokeDasharray={DASH}
                strokeLinejoin="round"
              />
            )}
            {chart.accountPath && (
              <path
                data-line="account"
                d={chart.accountPath}
                fill="none"
                stroke={ACCOUNT}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}
            {/* The one target: the plot and a little past it. A tap
                anywhere picks the nearest snapshot (nearestPoint); the
                marks drawn over it take no pointer. */}
            <rect
              data-chart-target
              x={chart.plot.left - REACH}
              y={chart.plot.top - REACH}
              width={chart.plot.right - chart.plot.left + 2 * REACH}
              height={chart.plot.bottom - chart.plot.top + 2 * REACH}
              fill="transparent"
              className="cursor-pointer"
              onClick={pick}
            />
            <g pointerEvents="none">
              {placedPick && placedPick !== latest && (
                <>
                  <circle cx={placedPick.x} cy={placedPick.yShadow} r={DOT} fill={SHADOW} stroke="var(--card)" strokeWidth={2} />
                  <circle cx={placedPick.x} cy={placedPick.yAccount} r={DOT} fill={ACCOUNT} stroke="var(--card)" strokeWidth={2} />
                </>
              )}
              <circle
                data-latest="shadow"
                cx={latest.x}
                cy={latest.yShadow}
                r={DOT}
                fill={SHADOW}
                stroke="var(--card)"
                strokeWidth={2}
              />
              <circle
                data-latest="account"
                cx={latest.x}
                cy={latest.yAccount}
                r={DOT}
                fill={ACCOUNT}
                stroke="var(--card)"
                strokeWidth={2}
              />
            </g>
          </svg>
        </div>
        {placedPick ? (
          <p data-testid="picked" className="m-0 text-sm tabular-nums">
            {formatInstant(placedPick.point.asOf)}: account {formatCents(placedPick.point.account)}, {shadowName}{" "}
            {formatCents(placedPick.point.shadow)} — {standing(placedPick.point.difference)}. Net contributions{" "}
            {formatCents(placedPick.point.net)}.
          </p>
        ) : (
          <p aria-hidden="true" className="m-0 text-xs text-muted-foreground">
            The latest snapshot is dotted. Tap or click the chart to see a snapshot&apos;s figures; Show as a table
            lists them all.
          </p>
        )}
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 md:h-8"
            aria-expanded={tableOpen}
            aria-controls={`${id}-table`}
            onClick={() => setTableOpen((open) => !open)}
          >
            <ChevronDown aria-hidden="true" className={cn("transition-transform", tableOpen && "rotate-180")} />
            {tableOpen ? "Hide the table" : "Show as a table"}
          </Button>
        </div>
        <div id={`${id}-table`} hidden={!tableOpen} className="mt-2">
          <table className="w-full border-collapse text-left text-sm tabular-nums">
            <caption className="sr-only">Account and {shadowName} at each snapshot, newest first</caption>
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th scope="col" className="py-1 pr-2 font-medium">
                  Snapshot
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  Account
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-medium">
                  {shadowName}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  Difference
                </th>
              </tr>
            </thead>
            <tbody>
              {[...series].reverse().map((point) => (
                <tr key={point.id} className="border-t align-baseline">
                  <td className="py-1 pr-2">{formatInstant(point.asOf)}</td>
                  <td className="py-1 pr-2 text-right">{formatCents(point.account)}</td>
                  <td className="py-1 pr-2 text-right">{formatCents(point.shadow)}</td>
                  <td className="py-1 text-right">{signedCents(point.difference)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {checked && !matches ? (
          <Alert variant="destructive" role="alert" className="mt-2">
            <AlertDescription>
              The line&apos;s last point doesn&apos;t match the scoreboard: the line has the account at{" "}
              {formatCents(last.account)}, the {shadowName} at {formatCents(last.shadow)} and net contributions at{" "}
              {formatCents(last.net)}; the scoreboard has {formatUsd(board.account_value)},{" "}
              {formatUsd(board.shadow_value)} and {formatUsd(board.net_contributions)}. The scoreboard&apos;s figures are the server&apos;s;
              the line is worked out in your browser — trust the scoreboard, and report this.
            </AlertDescription>
          </Alert>
        ) : (
          <p className="mt-2 mb-0 text-xs text-muted-foreground">
            Each point is a snapshot: the account&apos;s total value, and the same money held in{" "}
            {symbol ?? "the benchmark"} — worked out in your browser with the scoreboard&apos;s rules (How this is
            computed, above){checked ? "; its last point matches the scoreboard above to the cent." : "."}
          </p>
        )}
      </>
    );
  }
  const title = `Account vs ${symbol ?? "the benchmark"} over time`;
  return (
    <section aria-label={title} className="rounded-lg border bg-card p-4">
      <h2 className="mt-0 mb-1 text-lg font-semibold">{title}</h2>
      {body}
    </section>
  );
}

ScoreboardLine.propTypes = {
  /** useScoreboardSeries' state. */
  line: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    series: PropTypes.arrayOf(PropTypes.object),
    incomplete: PropTypes.bool.isRequired,
    truncated: PropTypes.bool.isRequired,
    snapshotsShown: PropTypes.number.isRequired,
    snapshotsTotal: PropTypes.number.isRequired,
    retry: PropTypes.func.isRequired,
  }).isRequired,
  /** GET scoreboard/'s data; null before the first snapshot or when it failed. */
  board: PropTypes.shape({
    snapshot: PropTypes.number.isRequired,
    benchmark_symbol: PropTypes.string.isRequired,
    account_value: PropTypes.string,
    shadow_value: PropTypes.string,
    net_contributions: PropTypes.string,
    difference: PropTypes.string,
  }),
};

export default ScoreboardLine;
